import React, { useState } from "react";
import ReactDOM from "react-dom";
import auth from "./auth";
import { useNavigate } from "react-router-dom";
import { db } from "../firebaseConfig";
import { doc, setDoc } from "firebase/firestore";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
} from "firebase/auth";

const overlayStyles = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0,0,0,0.5)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 1000,
};

const modalStyles = {
  backgroundColor: "#1e1e1e",
  padding: "20px",
  borderRadius: "8px",
  boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
  maxWidth: "400px",
  width: "90%",
  position: "relative", // Added for positioning the close button
};

const LoginModal = ({ isOpen, onClose }) => {
  const [signingUp, setSigningUp] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState("");
  const [resetStatus, setResetStatus] = useState("");

  const navigate = useNavigate();

  const handleLogin = async () => {
    setError("");
    setResetStatus("");
    try {
      const persistence = rememberMe
        ? browserLocalPersistence
        : browserSessionPersistence;
      await setPersistence(auth, persistence);
      await signInWithEmailAndPassword(auth, email, password);
      onClose();
      window.location.reload();
    } catch (err) {
      setError("Login failed. Please check your credentials.");
    }
  };

  const handleSignUp = async () => {
    setError("");
    setResetStatus("");
    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );
      const user = userCredential.user;

      const userDoc = doc(db, "users", user.uid);
      await setDoc(userDoc, {
        email: user.email,
        userID: user.uid,
        createdAt: new Date().toISOString(),
        userName: name,
      });

      setName("");
      onClose();
      window.location.reload();
    } catch (err) {
      setError("Could not create account. " + err.message);
    }
  };

  const handlePasswordReset = async () => {
    if (!email) {
      setError("Please enter your email above before resetting password.");
      return;
    }
    setError("");
    setResetStatus("");
    try {
      await sendPasswordResetEmail(auth, email);
      setResetStatus("Password reset link sent to: " + email);
    } catch (err) {
      setError("Could not send password reset. " + err.message);
    }
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div style={overlayStyles}>
      <div style={modalStyles}>
        {/* Close Button */}
        <button
          onClick={onClose}
          style={{
            position: "absolute",
            top: "10px",
            right: "10px",
            background: "transparent",
            border: "none",
            color: "#fff",
            fontSize: "1.5rem",
            cursor: "pointer",
          }}
          aria-label="Close"
        >
          &times;
        </button>

        <h1 style={{ textAlign: "center", color: "#18A0FF", marginBottom: "10px" }}>
          GEARSHARE
        </h1>
        <h2 style={{ textAlign: "center", marginBottom: "20px" }}>
          {signingUp ? "Create an Account" : "Login"}
        </h2>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            signingUp ? handleSignUp() : handleLogin();
          }}
          style={{ display: "flex", flexDirection: "column", gap: "12px" }}
        >
          {signingUp && (
            <label style={{ color: "white", fontWeight: "bold" }}>
              Name:
              <input
                type="text"
                style={{ width: "95%", padding: "8px", marginTop: "4px" }}
                onChange={(e) => setName(e.target.value)}
                value={name}
                placeholder="Enter your name"
                required
              />
            </label>
          )}
          <label style={{ color: "white", fontWeight: "bold" }}>
            Email:
            <input
              type="email"
              style={{ width: "95%", padding: "8px", marginTop: "4px" }}
              onChange={(e) => setEmail(e.target.value)}
              value={email}
              required
              placeholder="Enter your email"
            />
          </label>
          <label style={{ color: "white", fontWeight: "bold" }}>
            Password:
            <input
              type="password"
              style={{ width: "95%", padding: "8px", marginTop: "4px" }}
              onChange={(e) => setPassword(e.target.value)}
              value={password}
              required
              placeholder="Enter your password"
            />
          </label>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              color: "white",
              fontWeight: "bold",
            }}
          >
            <input
              type="checkbox"
              onChange={(e) => setRememberMe(e.target.checked)}
              checked={rememberMe}
            />
            Remember Me
          </label>
          <button
            type="submit"
            style={{
              padding: "10px",
              backgroundColor: "#007bff",
              color: "#fff",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontWeight: "bold",
            }}
          >
            {signingUp ? "Sign Up" : "Login"}
          </button>
        </form>
        <p style={{ textAlign: "center", margin: "20px 0", color: "#666" }}>OR</p>
        {signingUp ? (
          <button
            onClick={() => setSigningUp(false)}
            style={{
              width: "100%",
              padding: "10px",
              backgroundColor: "#28a745",
              color: "#fff",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontWeight: "bold",
              marginBottom: "20px",
            }}
          >
            Back to Login
          </button>
        ) : (
          <button
            onClick={() => setSigningUp(true)}
            style={{
              width: "100%",
              padding: "10px",
              backgroundColor: "#28a745",
              color: "#fff",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontWeight: "bold",
              marginBottom: "20px",
            }}
          >
            Create New Account
          </button>
        )}
        <div style={{ textAlign: "center", marginBottom: "20px" }}>
          <button
            onClick={handlePasswordReset}
            style={{
              background: "none",
              border: "none",
              color: "#00c9ff",
              textDecoration: "underline",
              cursor: "pointer",
            }}
          >
            Forgot Password?
          </button>
        </div>
        {error && <p style={{ color: "red", textAlign: "center" }}>{error}</p>}
        {resetStatus && (
          <div
            style={{
              backgroundColor: "#caf0f8",
              color: "#03045e",
              padding: "8px",
              borderRadius: "4px",
              textAlign: "center",
              marginBottom: "20px",
            }}
          >
            {resetStatus}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default LoginModal;
